import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Cpu } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

/* ── types ────────────────────────────────────────────────────────────────── */
interface TokenUsageRecord {
  record_id?: string; timestamp?: string; timestamp_request?: string; timestamp_response?: string;
  user_id?: string; client_id?: string; project_id?: string; sub_project_id?: string;
  model?: string; model_id?: string; prompt_name?: string;
  input_tokens?: number; output_tokens?: number; total_tokens?: number;
  tokens_input_cached?: number; tokens_input_uncached?: number; tokens_output?: number;
  estimated_cost_cents?: number; estimated_cost_usd?: number; execution_mode?: string;
}

interface LLMModel {
  model_id: string; model_host: string; model_name: string; billing_type: string;
  pricing: { input_cached?: number; input_uncached?: number; output?: number; per_1000_pages?: number; per_million_chars?: number; per_minute?: number };
  batch_reduction: number; context_size: number; effective_date: string;
}

interface VoiceRecord {
  record_id?: string; timestamp_request?: string; timestamp_response?: string;
  user_id?: string; client_id?: string; project_id?: string; sub_project_id?: string;
  model_id?: string; model_host?: string; usage_type?: string;
  characters_processed?: number; seconds_processed?: number;
  estimated_cost_cents?: number;
}

/* ── helpers ──────────────────────────────────────────────────────────────── */
const fmtTs = (ts?: string) => {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};
const fmtCost = (r: TokenUsageRecord) => {
  if (r.estimated_cost_cents !== undefined) return `$${(r.estimated_cost_cents / 100).toFixed(4)}`;
  if (r.estimated_cost_usd !== undefined) return `$${r.estimated_cost_usd.toFixed(4)}`;
  return '—';
};
const totalTokens = (r: TokenUsageRecord) => {
  if (r.total_tokens !== undefined) return r.total_tokens;
  if (r.tokens_input_cached !== undefined || r.tokens_input_uncached !== undefined || r.tokens_output !== undefined)
    return (r.tokens_input_cached ?? 0) + (r.tokens_input_uncached ?? 0) + (r.tokens_output ?? 0);
  return (r.input_tokens ?? 0) + (r.output_tokens ?? 0);
};
const inputDisplay = (r: TokenUsageRecord) => {
  if (r.tokens_input_cached !== undefined || r.tokens_input_uncached !== undefined) {
    const c = r.tokens_input_cached ?? 0, u = r.tokens_input_uncached ?? 0;
    return c > 0 ? `${c + u} (${c} cached)` : `${u}`;
  }
  return `${r.input_tokens ?? 0}`;
};
const shortId = (id?: string) => id ? `${id.substring(0, 8)}…` : '—';

/* ── component ────────────────────────────────────────────────────────────── */
export default function AdminTokenUsage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canReseed = user && ['SysAdmin', 'DevOps'].includes(user.role ?? '');

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [records, setRecords] = useState<TokenUsageRecord[]>([]);
  const [recordCount, setRecordCount] = useState(0);
  const [voiceRecords, setVoiceRecords] = useState<VoiceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [models, setModels] = useState<LLMModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  /* ── fetch records ──────────────────────────────────────────────────────── */
  const fetchRecords = async () => {
    if (!date) { setError(t('pages.adminTokenUsage.selectDateFirst')); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/token-usage/daily?date=${date}`);
      setRecords(data.records ?? []);
      setRecordCount(data.record_count ?? 0);
      setVoiceRecords(data.voice_records ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg ?? t('pages.adminTokenUsage.errorFetch'));
      setRecords([]); setRecordCount(0); setVoiceRecords([]);
    } finally { setLoading(false); }
  };

  /* ── fetch models ───────────────────────────────────────────────────────── */
  const fetchModels = async () => {
    setModelsLoading(true); setModelsError(null);
    try {
      const { data } = await api.get('/token-usage/models');
      setModels(data.models ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setModelsError(msg ?? t('pages.adminTokenUsage.errorFetchModels'));
      setModels([]);
    } finally { setModelsLoading(false); }
  };

  /* ── reseed ─────────────────────────────────────────────────────────────── */
  const reseedModels = async () => {
    if (!window.confirm(t('pages.adminTokenUsage.syncConfirm'))) return;
    setModelsLoading(true); setModelsError(null);
    try {
      const { data } = await api.post('/token-usage/reseed');
      const d = data.details;
      alert(t('pages.adminTokenUsage.syncSuccess', { added: d.success, updated: d.updated, skipped: d.skipped, errors: d.errors }));
      await fetchModels();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setModelsError(msg ?? t('pages.adminTokenUsage.errorSync'));
    } finally { setModelsLoading(false); }
  };

  /* ── columns ────────────────────────────────────────────────────────────── */
  const recCols: ColumnDef<TokenUsageRecord>[] = [
    { key: 'timestamp_request', label: t('pages.adminTokenUsage.timestamp'), sortable: true,
      render: r => <span className="font-mono text-xs">{fmtTs(r.timestamp_request ?? r.timestamp)}</span> },
    { key: 'user_id', label: t('pages.adminTokenUsage.userId'),
      render: r => <span className="font-mono text-xs" title={r.user_id}>{shortId(r.user_id)}</span> },
    { key: 'project_id', label: t('pages.adminTokenUsage.projectId'),
      render: r => <span className="font-mono text-xs" title={r.project_id ?? r.sub_project_id}>{shortId(r.project_id ?? r.sub_project_id)}</span> },
    { key: 'model_id', label: t('pages.adminTokenUsage.model'), sortable: true,
      render: r => <span className="text-xs">{r.model_id ?? r.model ?? '—'}</span> },
    { key: 'prompt_name', label: t('pages.adminTokenUsage.prompt'),
      render: r => <span className="text-xs text-slate-500">{r.prompt_name ?? '—'}</span> },
    { key: 'input_tokens', label: t('pages.adminTokenUsage.inputTokens'),
      render: r => <span className="text-xs tabular-nums">{inputDisplay(r)}</span> },
    { key: 'tokens_output', label: t('pages.adminTokenUsage.outputTokens'),
      render: r => <span className="text-xs tabular-nums">{r.tokens_output ?? r.output_tokens ?? 0}</span> },
    { key: 'total_tokens', label: t('pages.adminTokenUsage.total'),
      render: r => <span className="text-xs font-medium tabular-nums">{totalTokens(r)}</span> },
    { key: 'estimated_cost_cents', label: t('pages.adminTokenUsage.cost'), sortable: true,
      render: r => <span className="text-xs tabular-nums">{fmtCost(r)}</span> },
  ];

  const modelCols: ColumnDef<LLMModel>[] = [
    { key: 'model_id', label: t('pages.adminTokenUsage.modelId'), sortable: true,
      render: m => <span className="font-mono text-xs">{m.model_id}</span> },
    { key: 'model_host', label: t('pages.adminTokenUsage.host'), sortable: true,
      render: m => <Badge variant="outline" className="text-[11px]">{m.model_host}</Badge> },
    { key: 'model_name', label: t('pages.adminTokenUsage.modelName'), sortable: true },
    { key: 'billing_type', label: t('pages.adminTokenUsage.billingType'),
      render: m => <span className="text-xs text-slate-500">{m.billing_type}</span> },
    { key: 'pricing', label: t('pages.adminTokenUsage.pricing'),
      render: m => {
        if (m.billing_type === 'per_token')
          return <div className="text-[11px] leading-4 text-slate-600">
            <div>In(c): {m.pricing.input_cached ?? 0}¢/M</div>
            <div>In(u): {m.pricing.input_uncached ?? 0}¢/M</div>
            <div>Out: {m.pricing.output ?? 0}¢/M</div>
          </div>;
        if (m.billing_type === 'per_page')
          return <span className="text-xs">{m.pricing.per_1000_pages}¢/1K pg</span>;
        if (m.billing_type === 'per_character')
          return <span className="text-xs">{m.pricing.per_million_chars}¢/M chars</span>;
        if (m.billing_type === 'per_second')
          return <span className="text-xs">{m.pricing.per_minute}¢/min</span>;
        return <span className="text-xs">—</span>;
      } },
    { key: 'batch_reduction', label: t('pages.adminTokenUsage.batchDiscount'),
      render: m => <span className="text-xs">{m.batch_reduction}%</span> },
    { key: 'context_size', label: t('pages.adminTokenUsage.contextSize'), sortable: true,
      render: m => <span className="text-xs tabular-nums">{m.context_size.toLocaleString()}</span> },
    { key: 'effective_date', label: t('pages.adminTokenUsage.effectiveDate'), sortable: true,
      render: m => <span className="text-xs">{m.effective_date}</span> },
  ];

  /* ── render ─────────────────────────────────────────────────────────────── */
  return (
    <>
      <PageHeader title={t('pages.adminTokenUsage.title')} />

      <div className="px-6 pb-6 space-y-6">
        {/* Section 1: Token Usage Records */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.adminTokenUsage.sectionRecords')}</h2>

          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <label htmlFor="date-input" className="text-xs text-slate-500">{t('pages.adminTokenUsage.selectDate')}</label>
              <input
                id="date-input"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-slate-400"
              />
            </div>
            <Button size="sm" onClick={fetchRecords} disabled={loading || !date}>
              {loading ? t('pages.adminTokenUsage.fetching') : t('pages.adminTokenUsage.fetch')}
            </Button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {recordCount > 0 && (
            <p className="text-xs text-slate-500">
              {t('pages.adminTokenUsage.foundRecords', { count: recordCount, date })}
            </p>
          )}

          {records.length > 0 ? (
            <DataTable columns={recCols} data={records} />
          ) : !loading && (
            <EmptyState
              icon={BarChart3}
              title={t('pages.adminTokenUsage.noRecords')}
              description={t('pages.adminTokenUsage.noRecordsDescription')}
            />
          )}
        </section>

        <Separator />

        {/* Section 1b: Voice Usage Records */}
        {(voiceRecords.length > 0 || recordCount > 0) && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.adminTokenUsage.sectionVoice')}</h2>
            {voiceRecords.length > 0 ? (
              <DataTable
                columns={[
                  { key: 'timestamp_request', label: t('pages.adminTokenUsage.timestamp'), sortable: true,
                    render: (r: VoiceRecord) => <span className="font-mono text-xs">{fmtTs(r.timestamp_request)}</span> },
                  { key: 'user_id', label: t('pages.adminTokenUsage.userId'),
                    render: (r: VoiceRecord) => <span className="font-mono text-xs" title={r.user_id}>{shortId(r.user_id)}</span> },
                  { key: 'usage_type', label: 'Type', sortable: true,
                    render: (r: VoiceRecord) => <Badge variant="outline" className="text-[11px]">{r.usage_type?.toUpperCase() ?? '—'}</Badge> },
                  { key: 'model_id', label: t('pages.adminTokenUsage.model'),
                    render: (r: VoiceRecord) => <span className="text-xs">{r.model_id ?? '—'}</span> },
                  { key: 'characters_processed', label: 'Chars',
                    render: (r: VoiceRecord) => <span className="text-xs tabular-nums">{r.characters_processed ?? 0}</span> },
                  { key: 'seconds_processed', label: 'Seconds',
                    render: (r: VoiceRecord) => <span className="text-xs tabular-nums">{r.seconds_processed != null ? r.seconds_processed.toFixed(1) : '—'}</span> },
                  { key: 'estimated_cost_cents', label: t('pages.adminTokenUsage.cost'), sortable: true,
                    render: (r: VoiceRecord) => <span className="text-xs tabular-nums">{r.estimated_cost_cents != null ? `$${(r.estimated_cost_cents / 100).toFixed(4)}` : '—'}</span> },
                ]}
                data={voiceRecords}
              />
            ) : (
              <p className="text-xs text-slate-400">{t('pages.adminTokenUsage.noVoiceRecords')}</p>
            )}
          </section>
        )}

        <Separator />

        {/* Section 2: LLM Models Pricing */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 pb-1 border-b border-slate-200">{t('pages.adminTokenUsage.sectionModels')}</h2>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={fetchModels} disabled={modelsLoading}>
              {modelsLoading ? t('pages.adminTokenUsage.loading') : t('pages.adminTokenUsage.fetchModels')}
            </Button>
            {canReseed && (
              <Button size="sm" variant="ghost" onClick={reseedModels} disabled={modelsLoading}>
                {modelsLoading ? t('pages.adminTokenUsage.syncing') : t('pages.adminTokenUsage.syncModels')}
              </Button>
            )}
          </div>

          {modelsError && <p className="text-sm text-red-600">{modelsError}</p>}

          {models.length > 0 ? (
            <>
              <p className="text-xs text-slate-500">{models.length} model{models.length !== 1 ? 's' : ''}</p>
              <DataTable columns={modelCols} data={models} />
            </>
          ) : !modelsLoading && (
            <EmptyState
              icon={Cpu}
              title={t('pages.adminTokenUsage.noModels')}
              description={t('pages.adminTokenUsage.noModelsDescription')}
            />
          )}
        </section>
      </div>
    </>
  );
}

